import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import useProgressiveImg from 'hooks/use-progressive-image';
import type { ContentImage } from 'components/pdp/modules/types';

const ParallaxWrapper = styled.div`
  position: absolute;
  inset: 0;
`;

const ParallaxImage = styled.img`
  display: block;
  position: absolute;
  inset: 0;
  transform: scale(1.2);
  width: 100%;
  height: 100%;
  z-index: -1;
  object-fit: cover;
  object-position: 50% var(--parallax-scroll-offset);
  transition: object-position 0.05s;
`;

export default function ParallaxBackground({
  mediaItem,
}: {
  mediaItem: ContentImage;
}) {
  const { ref, inView, entry } = useInView({});

  const currentMediaImage = useProgressiveImg({
    mediaItem: mediaItem,
  });

  // Run effect everytime component enters/exits viewport for adding or removing event
  //  to prevent running scroll event listener if component isn't even in view
  useEffect(() => {
    // clean up so we don't end up with multiple listeners for scroll events
    window.removeEventListener('scroll', updateImagePositioning);
    if (inView) {
      window.addEventListener('scroll', updateImagePositioning, {
        passive: true,
      });
    }
    return () => window.removeEventListener('scroll', updateImagePositioning);
  }, [inView]);

  const updateImagePositioning = () => {
    if (entry) {
      const documentPosition = document.documentElement.clientHeight / 2;
      const imageInfo = currentMediaImage.entry.target.getBoundingClientRect();
      const imagePosition = imageInfo.height / 2 + imageInfo.top;
      const position =
        documentPosition !== 0
          ? ((documentPosition - imagePosition) * 65.0) / documentPosition
          : 100;
      currentMediaImage.entry.target.style.setProperty(
        '--parallax-scroll-offset',
        `${Math.max(-100, Math.min(position, 100)) / 2.0 + 50}%`,
      );
    }
  };

  return (
    <ParallaxWrapper ref={ref}>
      <ParallaxImage
        ref={currentMediaImage.ref}
        src={currentMediaImage.src}
        alt={mediaItem.alt}
        style={{
          '--parallax-scroll-offset': '50%',
          ...currentMediaImage.blurStyles,
        }}
      />
    </ParallaxWrapper>
  );
}
